import { Users, Activity, Link, BarChart3, Plus, Home, Bell, Share2, Coins, Gift, Crown, Video } from 'lucide-react';
import { RealTimeNotifications } from './RealTimeNotifications';
import { NavLink, useLocation } from 'react-router-dom';
import {
  Sidebar,
  SidebarContent,
  SidebarGroup,
  SidebarGroupContent,
  SidebarGroupLabel,
  SidebarMenu,
  SidebarMenuItem,
  SidebarTrigger,
  useSidebar,
} from '@/components/ui/sidebar';
import { cn } from '@/lib/utils';

// Organized navigation sections for better practitioner UX
const dashboardItem = { title: 'Dashboard', url: '/practitioner', icon: Home, end: true };

const clientWorkItems = [
  { title: 'Clients', url: '/practitioner/clients', icon: Users, end: false },
  { title: 'Create Session', url: '/practitioner/sessions', icon: Plus, end: false },
  { title: 'All Sessions', url: '/practitioner/all-sessions', icon: Activity, end: false },
  { title: 'Session Links', url: '/practitioner/links', icon: Link, end: false },
];

const businessItems = [
  { title: 'Credits & Billing', url: '/practitioner/credits', icon: Coins, end: false },
  { title: 'Perks', url: '/practitioner/perks', icon: Gift, end: false },
  { title: 'Referrals', url: '/practitioner/referrals', icon: Share2, end: false },
];

const toolsItems = [
  { title: 'Analytics', url: '/practitioner/analytics', icon: BarChart3, end: false },
  { title: 'Social Shorts', url: '/practitioner/shorts', icon: Video, end: false },
  { title: 'Notifications', url: '/practitioner/notifications', icon: Bell, end: false },
];

export function PractitionerSidebar() {
  const { state } = useSidebar();
  const collapsed = state === 'collapsed';

  // Helper to get base NavLink classes
  const getBaseNavLinkClasses = () => {
    return "flex w-full items-center gap-2 overflow-hidden rounded-md p-2 text-left text-sm outline-none transition-all duration-200 [&>span:last-child]:truncate [&>svg]:size-4 [&>svg]:shrink-0";
  };

  const getNavClassName = ({ isActive }: { isActive: boolean }) => {
    const activeClass = isActive ? 'active' : '';

    // All menu items use the same consistent theme highlight
    if (isActive) {
      return `mx-2 mb-1 rounded-lg ${activeClass} bg-accent text-accent-foreground font-semibold border-l-4 border-primary`;
    }
    return `mx-2 mb-1 rounded-lg hover:bg-accent/50 hover:text-accent-foreground`;
  };

  return (
    <Sidebar className={collapsed ? 'w-14' : 'w-60'} collapsible="icon">
      <div className="p-4 border-b border-sidebar-border">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2">
            <Activity className="h-6 w-6 text-primary" />
            {!collapsed && (
              <h2 className="font-semibold text-sidebar-foreground">Practitioner</h2>
            )}
          </div>
          {!collapsed && <RealTimeNotifications />}
        </div>
      </div>

      <SidebarContent>
        {/* Dashboard */}
        <SidebarGroup>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <NavLink
                  to={dashboardItem.url}
                  end={dashboardItem.end}
                  className={({ isActive }) => cn(
                    getBaseNavLinkClasses(),
                    getNavClassName({ isActive })
                  )}
                >
                  <dashboardItem.icon className="h-4 w-4" />
                  {!collapsed && <span>{dashboardItem.title}</span>}
                </NavLink>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Client Work Section */}
        <SidebarGroup>
          <SidebarGroupLabel className={collapsed ? 'sr-only' : ''}>
            👥 Client Work
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {clientWorkItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <NavLink
                    to={item.url}
                    end={item.end}
                    className={({ isActive }) => cn(
                      getBaseNavLinkClasses(),
                      getNavClassName({ isActive })
                    )}
                  >
                    <item.icon className="h-4 w-4" />
                    {!collapsed && <span>{item.title}</span>}
                  </NavLink>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Business Section */}
        <SidebarGroup>
          <SidebarGroupLabel className={collapsed ? 'sr-only' : ''}>
            💼 Business
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {businessItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <NavLink
                    to={item.url}
                    end={item.end}
                    className={({ isActive }) => cn(
                      getBaseNavLinkClasses(),
                      getNavClassName({ isActive })
                    )}
                  >
                    <item.icon className="h-4 w-4" />
                    {!collapsed && <span>{item.title}</span>}
                  </NavLink>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Tools Section */}
        <SidebarGroup>
          <SidebarGroupLabel className={collapsed ? 'sr-only' : ''}>
            🔧 Tools
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              {toolsItems.map((item) => (
                <SidebarMenuItem key={item.title}>
                  <NavLink
                    to={item.url}
                    end={item.end}
                    className={({ isActive }) => cn(
                      getBaseNavLinkClasses(),
                      getNavClassName({ isActive })
                    )}
                  >
                    <item.icon className="h-4 w-4" />
                    {!collapsed && <span>{item.title}</span>}
                  </NavLink>
                </SidebarMenuItem>
              ))}
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>

        {/* Main App Link */}
        <SidebarGroup>
          <SidebarGroupLabel className={collapsed ? 'sr-only' : ''}>
            🏠 Account
          </SidebarGroupLabel>
          <SidebarGroupContent>
            <SidebarMenu>
              <SidebarMenuItem>
                <NavLink
                  to="/app"
                  className={({ isActive }) => cn(
                    getBaseNavLinkClasses(),
                    getNavClassName({ isActive })
                  )}
                >
                  <Home className="h-4 w-4" />
                  {!collapsed && <span>Return to Home</span>}
                </NavLink>
              </SidebarMenuItem>
            </SidebarMenu>
          </SidebarGroupContent>
        </SidebarGroup>
      </SidebarContent>
    </Sidebar>
  );
}